import React, { useEffect, useMemo, useRef } from 'react';

import type { EditEntryOrGroup } from '@aline/common';
import { groupEditEntries, isEditEntryGroup } from '@aline/common';
import { Typography } from '@mui/material';
import { equals, isEmpty } from 'ramda';
import type { IndexLocationWithAlign, VirtuosoHandle } from 'react-virtuoso';
import { Virtuoso } from 'react-virtuoso';
import { Range } from 'slate';
import { PanelHeader } from 'src/components';
import { TrackedChangesEmptyIcon } from 'src/components/icons/TrackedChangesEmptyIcon';
import {
  useEditorTabStateActions,
  useEditorTabStateSelector,
} from 'src/editor/editor-tab-state-registry';
import { useEditorLoading, useMaybeEditor } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';

import { TrackedChangesBox } from './TrackedChangesBox';
import { useBackToAI } from '../BackToAIButton';

const useStyles = makeStyles()(theme => ({
  empty: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(7),
  },
  emptyIcon: {
    marginBottom: theme.spacing(2),
  },
  footer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1, 0, 2, 0),
  },
  panelContainer: {
    backgroundColor: theme.appColors.background,
    height: '100%',
    // Unset overflowY from the parent as react-virtuoso takes care of scrolling and we don't want overlapping scroll containers.
    overflowY: 'unset',
    width: '100%',
  },
}));

export function TrackedChangesDrawer(): JSX.Element {
  const { classes } = useStyles();
  const loading = useEditorLoading();
  const handleBackToAI = useBackToAI();
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);
  /**
   * Whether the list was already on screen before the current commit. A list that mounts now has
   * no measured items yet, so it is positioned through `initialTopMostItemIndex` instead of
   * `scrollToIndex`, which would aim at estimated offsets.
   */
  const listShownRef = useRef(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const editor = useMaybeEditor();
  const activeEdits = useEditorTabStateSelector(state => state.derived.activeEdits, equals);
  const suggestedEdits = useEditorTabStateSelector(state => state.derived.suggestedEdits, equals);
  const scrollRequest = useEditorTabStateSelector(state => state.trackedChanges.scrollRequest);
  const {
    trackedChanges: { clearScrollRequest },
  } = useEditorTabStateActions();
  const groupedEdits = useMemo(
    () => (editor ? groupEditEntries(editor, suggestedEdits) : []),
    [suggestedEdits, editor]
  );

  const showList = !loading && !isEmpty(groupedEdits);
  const activeIndex = useMemo(() => {
    if (!activeEdits) return -1;

    return groupedEdits.findIndex(item => {
      if (isEditEntryGroup(item)) {
        return item.editEntries.some(entry => activeEdits.some(edit => equals(edit, entry)));
      }
      return activeEdits.some(edit => equals(edit, item));
    });
  }, [activeEdits, groupedEdits]);
  const requestedIndex = useMemo(() => {
    if (!scrollRequest) return -1;
    return groupedEdits.findIndex(item => getEditItemHasRange(item, scrollRequest.target));
  }, [groupedEdits, scrollRequest]);

  useEffect(() => {
    if (!virtuosoRef.current || !listShownRef.current || activeIndex === -1) return;

    virtuosoRef.current.scrollToIndex({ align: 'center', behavior: 'smooth', index: activeIndex });
  }, [activeIndex]);

  useEffect(() => {
    // A request for a change the list doesn't hold yet stays queued until the list catches up.
    if (!virtuosoRef.current || !scrollRequest || requestedIndex === -1) return;

    clearScrollRequest(scrollRequest.requestId);
    if (listShownRef.current)
      virtuosoRef.current.scrollToIndex({
        align: 'center',
        behavior: 'smooth',
        index: requestedIndex,
      });
  }, [clearScrollRequest, requestedIndex, scrollRequest]);

  useEffect(() => {
    listShownRef.current = showList;
  }, [showList]);

  if (loading)
    return (
      <div className={classes.panelContainer}>
        <PanelHeader
          showBackButton
          backButtonTooltip="Back to AI"
          text="Tracked Changes"
          onClickBack={handleBackToAI}
        />
      </div>
    );

  if (isEmpty(groupedEdits))
    return (
      <div className={classes.panelContainer}>
        <PanelHeader
          showBackButton
          backButtonTooltip="Back to AI"
          text="Tracked Changes"
          onClickBack={handleBackToAI}
        />
        <div className={classes.empty}>
          <TrackedChangesEmptyIcon classes={{ root: classes.emptyIcon }} />
          <Typography variant="subtitle2">No changes made</Typography>
        </div>
      </div>
    );

  return (
    <div className={classes.panelContainer}>
      <PanelHeader
        ref={panelRef}
        showBackButton
        backButtonTooltip="Back to AI"
        text="Tracked Changes"
        onClickBack={handleBackToAI}
      />
      <Virtuoso
        ref={virtuosoRef}
        components={{
          Footer: TrackedChangesFooter,
        }}
        initialTopMostItemIndex={getInitialTopMostItemIndex(requestedIndex, activeIndex)}
        itemContent={(index: number): JSX.Element => {
          const item = groupedEdits[index];
          const isActiveEdit = isEditEntryGroup(item)
            ? (activeEdits?.some(activeEdit =>
                item.editEntries.some(entry => equals(entry, activeEdit))
              ) ?? false)
            : (activeEdits?.some(edit => equals(edit, item)) ?? false);

          return <TrackedChangesBox key={index} activeEdit={isActiveEdit} editEntry={item} />;
        }}
        style={{
          height: `calc(100% - ${panelRef.current?.getBoundingClientRect().height ?? 0}px)`,
          width: '100%',
        }}
        totalCount={groupedEdits.length}
      />
    </div>
  );
}

function TrackedChangesFooter(): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.footer}>
      <Typography variant="caption">No more changes</Typography>
    </div>
  );
}

function getEditItemHasRange(editItem: EditEntryOrGroup, range: Range) {
  if (isEditEntryGroup(editItem))
    return editItem.editEntries.some(editEntry => Range.equals(editEntry.range, range));
  return Range.equals(editItem.range, range);
}

function getInitialTopMostItemIndex(
  requestedIndex: number,
  activeIndex: number
): IndexLocationWithAlign | number {
  const index = requestedIndex === -1 ? activeIndex : requestedIndex;
  if (index === -1) return 0;
  return { align: 'center', index };
}
